import Button from '@/components/Button';
import ClientOnly from '@/components/ClientOnly';
import { Icon, IconVariant } from '@/components/Icon';
import Logo from '@/components/Logo';
import { LogoVariant } from '@/components/LogoVariant';
import Modal from '@/components/Modal';
import { getGameById } from '@/helpers/getGameById';
import { GameSwitchList } from '@/helpers/getGameLink';
import { getNextJackpot } from '@/helpers/getNextJackpot';
import classnames from '@/utilities/classnames';
import replaceAbbreviations from '@/utilities/replaceAbbreviations';
import { useEffect, useState } from 'react';

export default function GameHeader(props: {
  gameName: string;
  gameId: number;
  shortname: string;
  logo?: LogoVariant;
  jackpotDesc?: string;
  chanceDesc?: string;
  onClickBackButton?: () => void;
  backButtonLabel?: JSX.Element;
  gameSwitch: GameSwitchList;
  eurojackpotVariant?: boolean;
  showBackButton?: boolean;
}) {
  const [jackpotTitle, setJackpotTitle] = useState<string>('');
  const [jackpotTimes, setJackpotTimes] = useState<string>('');

  useEffect(() => {
    async function fetchData() {
      const gameConfig = getGameById(props.gameId);
      if (gameConfig) {
        const times = gameConfig?.jackpotTimes || '';
        setJackpotTimes(times);
        const data = await getNextJackpot(gameConfig);
        const title = data
          ? data.labelGame || data.label
          : props.jackpotDesc || '';
        setJackpotTitle(title);
      }
    }
    fetchData();
  }, [props]);

  const showSwitch: boolean = filteredGameSwitch()?.length > 0;

  function filteredGameSwitch(): GameSwitchList {
    return props.gameSwitch?.filter((game) => game.id !== props.gameId);
  }

  function hasAlternativeDesign(): boolean {
    const config = getGameById(props.gameId);
    return Boolean(config && config.alternativeDesign);
  }

  function handleOnGameSwitchClick(): void {
    Modal.open('ModalGameSwitch');
  }

  return (
    <header
      className={classnames('GameHeader', `GameHeader--${props.shortname}`, {
        'GameHeader--eurojackpot-external': props.eurojackpotVariant,
        'GameHeader--alternative': hasAlternativeDesign(),
      })}
    >
      <div className="GameHeader__background" />
      {hasAlternativeDesign() && (
        <div className="GameHeader__stage">
          <div className="GameHeader__stageBackground" />
          <div className="GameHeader__stageContent">
            <div className="GameHeader__mascot" />
          </div>
        </div>
      )}
      <div className="GameHeader__container">
        {props.showBackButton && props.backButtonLabel && (
          <div className="GameHeader__item">
            <Button
              variant={Button.Variant.Back}
              onClick={props.onClickBackButton}
            >
              {props.backButtonLabel}
            </Button>
          </div>
        )}
        <div className="GameHeader__item--middle">
          {props.eurojackpotVariant ? (
            <img
              src="/assets/images/logo-eurojackpot-claim.webp"
              alt="Eurojackpot Logo"
              className="GameHeader__eurojackpot-external-logo"
            />
          ) : (
            <>
              <div className="GameHeader__logo--wrapper">
                {props.logo ? (
                  <Logo
                    variant={props.logo}
                    className="GameHeader__logo"
                    title={`${props.gameName} online spielen`}
                  />
                ) : (
                  props.gameName
                )}
              </div>
              <div className="GameHeader__jackpotData">
                <div className="GameHeader__description">
                  {props.gameId === 2 && jackpotTimes && (
                    <span
                      className="GameHeader__subtitle"
                      dangerouslySetInnerHTML={replaceAbbreviations(
                        jackpotTimes
                      )}
                    />
                  )}
                  <span
                    className="GameHeader__jackpotDesc"
                    dangerouslySetInnerHTML={replaceAbbreviations(
                      `&nbsp;${jackpotTitle}&nbsp;`
                    )}
                  />
                  <span
                    className="GameHeader__chanceDesc"
                    dangerouslySetInnerHTML={replaceAbbreviations(
                      props.chanceDesc || ''
                    )}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <ClientOnly>
          {showSwitch && (
            <div className="GameHeader__item GameHeader__item--right">
              <Button
                variant={Button.Variant.Back}
                onClick={handleOnGameSwitchClick}
              >
                <Icon variant={IconVariant.Tiles} />
                <span className="h-hide-xxs--down">Lotterie wechseln</span>
              </Button>
            </div>
          )}
        </ClientOnly>
      </div>
    </header>
  );
}
